/**
 * AuthContext - Global authentication state management
 * 
 * Provides authentication state, login/logout functions,
 * and user information throughout the application.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';

// Create the authentication context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Epoch counter to prevent stale async callbacks from overwriting auth state.
  // Any auth action (login, logout, checkSession, getCurrentUser) captures the
  // current epoch; if another auth action bumps it before the first completes,
  // the stale callback silently drops its state update. This eliminates the
  // login-loop race where checkSession (fired on mount) returns after login
  // succeeds and wipes isAuthenticated back to false.
  const authEpochRef = useRef(0);

  // Retry helper for transient failures (503, 502, 504)
  const fetchWithRetry = useCallback(async (url, options, maxRetries = 3) => {
    let lastError;
    for (let i = 0; i < maxRetries; i++) {
      try {
        const response = await fetch(url, options);
        if (response.status === 503 || response.status === 502 || response.status === 504) {
          lastError = new Error(`Server temporarily unavailable (${response.status})`);
          if (i < maxRetries - 1) {
            await new Promise(r => setTimeout(r, 300 * (i + 1))); // exponential backoff
            continue;
          }
        }
        return response;
      } catch (err) {
        lastError = err;
        if (i < maxRetries - 1) {
          await new Promise(r => setTimeout(r, 300 * (i + 1)));
        }
      }
    }
    throw lastError;
  }, []);

  // Check session on mount
  useEffect(() => {
    checkSession();
  }, []);

  // Mid-session expiry: the axios instance in useApi.js dispatches
  // 'leadsource:unauthorized' when any authenticated API call gets a 401.
  // Clear auth state so ProtectedRoute redirects to /login instead of the
  // page sitting on "Error loading ..." messages.
  useEffect(() => {
    const handleUnauthorized = () => {
      ++authEpochRef.current; // invalidate in-flight auth callbacks
      setUser(null);
      setIsAuthenticated(false);
      setIsImpersonating(false);
      setImpersonatingUserId(null);
    };
    window.addEventListener('leadsource:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('leadsource:unauthorized', handleUnauthorized);
  }, []);

  // Check session status
  const checkSession = useCallback(async () => {
    const epoch = ++authEpochRef.current;
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithRetry(`${API_BASE_URL}/auth/session`, {
        method: 'GET',
        credentials: 'include', // Include cookies
        headers: {
          'Accept': 'application/json',
          ...(localStorage.getItem('impersonate_user_id') ? { 'X-View-As': localStorage.getItem('impersonate_user_id') } : {}),
        },
      });

      if (epoch !== authEpochRef.current) return;

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated && data.user) {
          setUser(data.user);
          setIsAuthenticated(true);
          if (data.is_impersonating) {
            setIsImpersonating(true);
            setImpersonatingUserId(data.real_user_id);
          } else {
            setIsImpersonating(false);
            setImpersonatingUserId(null);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsImpersonating(false);
          setImpersonatingUserId(null);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setIsImpersonating(false);
        setImpersonatingUserId(null);
      }
    } catch (err) {
      if (epoch !== authEpochRef.current) return;
      console.error('Session check error:', err);
      // On persistent failure, preserve existing auth state briefly to avoid flicker
      // Only clear auth if we were never authenticated
      setError('Failed to check session status');
    } finally {
      if (epoch === authEpochRef.current) {
        setIsLoading(false);
      }
    }
  }, [fetchWithRetry]);

  // Login function
  const login = useCallback(async (username, password, rememberMe = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('impersonate_user_id') ? { 'X-View-As': localStorage.getItem('impersonate_user_id') } : {}),
        },
        body: JSON.stringify({
          username,
          password,
          remember_me: rememberMe,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        ++authEpochRef.current; // invalidate any in-flight checkSession / getCurrentUser
        setUser(data.user);
        setIsAuthenticated(true);
        return { success: true, message: data.message };
      } else {
        setError(data.message || 'Login failed');
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = 'Network error. Please try again.';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      // Increment epoch so any in-flight checkSession / getCurrentUser is ignored
      ++authEpochRef.current;

      // Clear state regardless of response
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setIsImpersonating(false);
      setImpersonatingUserId(null);

      if (response.ok) {
        return { success: true };
      } else {
        return { success: false, message: 'Logout failed on server' };
      }
    } catch (err) {
      console.error('Logout error:', err);
      // Still clear local state
      ++authEpochRef.current;
      setUser(null);
      setIsAuthenticated(false);
      setIsImpersonating(false);
      setImpersonatingUserId(null);
      return { success: true }; // Considered successful from UX perspective
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get current user info
  const getCurrentUser = useCallback(async () => {
    const epoch = ++authEpochRef.current;
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          ...(localStorage.getItem('impersonate_user_id') ? { 'X-View-As': localStorage.getItem('impersonate_user_id') } : {}),
        },
      });

      if (epoch !== authEpochRef.current) return null;

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setIsAuthenticated(true);
        return data;
      } else if (response.status === 401) {
        setUser(null);
        setIsAuthenticated(false);
        return null;
      } else {
        throw new Error('Failed to fetch user info');
      }
    } catch (err) {
      if (epoch === authEpochRef.current) {
        console.error('Get current user error:', err);
      }
      return null;
    }
  }, []);

  // Check if user has admin role
  const isAdmin = useCallback(() => {
    return user?.is_admin || false;
  }, [user]);

  // Check if user is superadmin
  const isSuperAdmin = useCallback(() => {
    return user?.is_superadmin || false;
  }, [user]);

  // Check if user can view the Agents / Private Listers / FB Ads Leads pages.
  // No automatic admin bypass — toggled per-user (admins included) via the
  // Users & Permissions admin panel; see AdminUsers.jsx / the session's
  // `user.permissions` array.
  const canViewAgents = useCallback(() => {
    return (user?.permissions || []).includes('agents_view');
  }, [user]);

  // Check if user can access SMS compose/send features (same: no admin bypass).
  const canSendSms = useCallback(() => {
    return (user?.permissions || []).includes('sms_access');
  }, [user]);

  // Check if user has specific permission
  const hasPermission = useCallback((requiredRole) => {
    if (!user) return false;
    if (user.is_superadmin) return true; // Superadmin has all permissions
    if (requiredRole === 'admin') return user.is_admin;
    if (requiredRole === 'user') return true;
    return false;
  }, [user]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Start impersonating another user
  const startImpersonation = useCallback(async (userId) => {
    localStorage.setItem('impersonate_user_id', userId);
    window.location.reload();
  }, []);

  // Stop impersonating
  const stopImpersonation = useCallback(async () => {
    localStorage.removeItem('impersonate_user_id');
    window.location.reload();
  }, []);

  // Context value
  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    checkSession,
    getCurrentUser,
    isAdmin,
    isSuperAdmin,
    canViewAgents,
    canSendSms,
    hasPermission,
    clearError,
    isImpersonating,
    impersonatingUserId,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
